import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import useSubmitGuard from '../hooks/useSubmitGuard';
import useDeepLinkHighlight from '../hooks/useDeepLinkHighlight';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Plus, 
  ChevronDown, 
  ChevronRight, 
  Trash2, 
  Upload,
  Package,
  Loader2,
  AlertTriangle,
  Edit2,
  ShoppingCart,
  History
} from 'lucide-react';
import { format } from 'date-fns';
import {
  FilterBar,
  SearchFilter,
  DateRangeFilter,
  countActiveFilters
} from '../components/FilterComponents';
import { ExportButton } from '../utils/exportUtils';
import DeleteRequestDialog from '../components/DeleteRequestDialog';
import PendingDeletionsPanel from '../components/PendingDeletionsPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InventoryManagement = ({ user }) => {
  const canEdit = user?.is_admin || user?.permissions?.inventory?.edit;
  const canApprove = user?.is_admin || user?.is_super_admin;
  const guard = useSubmitGuard();

  // Delete approval
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [pendingDelRequests, setPendingDelRequests] = useState([]);

  const fetchPendingDeletions = async () => {
    if (!canApprove) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/delete-requests?status=pending&module=inventory`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingDelRequests(res.data);
    } catch {}
  };

  const handleApproveDelete = async (reqId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/delete-requests/${reqId}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deletion approved');
      fetchInventory();
      fetchPendingDeletions();
    } catch { toast.error('Failed to approve'); }
  };

  const handleRejectDelete = async (reqId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/delete-requests/${reqId}/reject`, { note: 'Rejected' }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Request rejected');
      fetchPendingDeletions();
    } catch { toast.error('Failed to reject'); }
  };

  const [activeTab, setActiveTab] = useState('stock');
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);

  useDeepLinkHighlight('sku', 'sku-row-', [loading, inventory.length]);

  const [expandedSKU, setExpandedSKU] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [loadingPurchases, setLoadingPurchases] = useState(false);
  const [invSearch, setInvSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 50;
  // Issues History state
  const [issues, setIssues] = useState([]);
  const [loadingIssues, setLoadingIssues] = useState(false);
  const [issuesDateRange, setIssuesDateRange] = useState({ from: null, to: null });
  const [issuesSearch, setIssuesSearch] = useState('');
  
  const [isAddSKUOpen, setIsAddSKUOpen] = useState(false);
  const [isEditSKUOpen, setIsEditSKUOpen] = useState(false);
  const [isAddPurchaseOpen, setIsAddPurchaseOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedSKU, setSelectedSKU] = useState(null);
  
  const [skuForm, setSkuForm] = useState({
    particulars: '',
    part_number: '',
    make: '',
    model: '',
    machinery_group: '',
    uom: "No's",
    description: '',
    vendor_name: '',
    min_quantity: '',
    current_quantity: '',
    unit_price: '',
    category: 'Spares'
  });

  const [purchaseForm, setPurchaseForm] = useState({
    quantity: '',
    unit_price: '',
    invoice_number: '',
    purchase_date: '',
    vendor_name: ''
  });

  useEffect(() => {
    fetchInventory();
    fetchPendingDeletions();
  }, []);

  useEffect(() => {
    if (activeTab === 'issues') fetchIssues();
  }, [activeTab, issuesDateRange]);

  const fetchIssues = async () => {
    setLoadingIssues(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (issuesDateRange.from) params.append('start_date', format(issuesDateRange.from, 'yyyy-MM-dd'));
      if (issuesDateRange.to) params.append('end_date', format(issuesDateRange.to, 'yyyy-MM-dd'));
      const res = await axios.get(`${API}/inventory/issues?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIssues(res.data);
    } catch (error) {
      toast.error('Failed to fetch inventory issues');
    } finally {
      setLoadingIssues(false);
    }
  };

  const fetchInventory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/inventory`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInventory(response.data);
    } catch (error) {
      toast.error('Failed to fetch inventory');
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchases = async (skuId) => {
    setLoadingPurchases(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/inventory/${skuId}/purchases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPurchases(response.data);
    } catch (error) {
      toast.error('Failed to fetch purchase history');
    } finally {
      setLoadingPurchases(false);
    }
  };

  const toggleExpand = (skuId) => {
    if (expandedSKU === skuId) {
      setExpandedSKU(null);
      setPurchases([]);
    } else {
      setExpandedSKU(skuId);
      fetchPurchases(skuId);
    }
  };

  const handleAddSKU = async (e) => {
    e.preventDefault();
    guard(async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/inventory`, skuForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('SKU added successfully!');
        resetSKUForm();
        setIsAddSKUOpen(false);
        fetchInventory();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to add SKU');
      }
    });
  };

  const handleEditSKU = async (e) => {
    e.preventDefault();
    guard(async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.put(`${API}/inventory/${selectedSKU.id}`, skuForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('SKU updated successfully!');
        resetSKUForm();
        setIsEditSKUOpen(false);
        setSelectedSKU(null);
        fetchInventory();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to update SKU');
      }
    });
  };

  const handleDeleteSKU = async (sku) => {
    if (canApprove) {
      if (!window.confirm(`Delete SKU "${sku.particulars}" and all its purchase history?`)) return;
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API}/inventory/${sku.id}`, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('SKU deleted!');
        if (expandedSKU === sku.id) { setExpandedSKU(null); setPurchases([]); }
        fetchInventory();
        fetchPendingDeletions();
      } catch { toast.error('Failed to delete SKU'); }
    } else {
      setDeleteTarget({ id: sku.id, display: `SKU: ${sku.particulars} (${sku.part_number || ''})` });
    }
  };

  const handleAddPurchase = async (e) => {
    e.preventDefault();
    guard(async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/inventory/${selectedSKU.id}/purchases`, {
          ...purchaseForm,
          sku_id: selectedSKU.id
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Purchase recorded!');
        resetPurchaseForm();
        setIsAddPurchaseOpen(false);
        fetchInventory();
        if (expandedSKU === selectedSKU.id) {
          fetchPurchases(selectedSKU.id);
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to record purchase');
      }
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/inventory/bulk-import`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(`Imported ${response.data.imported} SKUs!`);
      if (response.data.errors.length > 0) {
        toast.error(`${response.data.errors.length} rows had errors`);
      }
      setIsImportOpen(false);
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setUploading(false);
    }
  };

  const resetSKUForm = () => {
    setSkuForm({
      particulars: '',
      part_number: '',
      make: '',
      model: '',
      machinery_group: '',
      uom: "No's",
      description: '',
      vendor_name: '',
      min_quantity: '',
      current_quantity: '',
      unit_price: '',
      category: 'Spares'
    });
  };

  const resetPurchaseForm = () => {
    setPurchaseForm({
      quantity: '',
      unit_price: '',
      invoice_number: '',
      purchase_date: '',
      vendor_name: ''
    });
  };

  const openEditModal = async (sku) => {
    setSelectedSKU(sku);
    // Pre-fill with list data so modal opens instantly
    setSkuForm({
      particulars: sku.particulars,
      part_number: sku.part_number || '',
      make: sku.make || '',
      model: '',
      machinery_group: sku.machinery_group || '',
      uom: sku.uom || "No's",
      description: '',
      vendor_name: '',
      min_quantity: sku.min_quantity || 0,
      current_quantity: sku.current_quantity || 0,
      unit_price: sku.unit_price || sku.average_price || 0,
      category: sku.category || 'Spares'
    });
    setIsEditSKUOpen(true);
    // Fetch full details in background and update form
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/inventory/${sku.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const full = res.data;
      setSkuForm({
        particulars: full.particulars,
        part_number: full.part_number || '',
        make: full.make || '',
        model: full.model || '',
        machinery_group: full.machinery_group || '',
        uom: full.uom || "No's",
        description: full.description || '',
        vendor_name: full.vendor_name || '',
        min_quantity: full.min_quantity || 0,
        current_quantity: full.current_quantity || 0,
        unit_price: full.unit_price || full.average_price || 0,
        category: full.category || 'Spares'
      });
    } catch {
      // modal is already open with partial data — user can still edit key fields
    }
  };

  const openPurchaseModal = (sku) => {
    setSelectedSKU(sku);
    resetPurchaseForm();
    setIsAddPurchaseOpen(true);
  };

  const isLowStock = (sku) => {
    return sku.min_quantity > 0 && sku.current_quantity <= sku.min_quantity;
  };

  const filteredInventory = useMemo(() => {
    setCurrentPage(1);
    if (!invSearch.trim()) return inventory;
    const q = invSearch.toLowerCase();
    return inventory.filter(sku =>
      sku.particulars?.toLowerCase().includes(q) ||
      sku.part_number?.toLowerCase().includes(q) ||
      sku.make?.toLowerCase().includes(q) ||
      sku.machinery_group?.toLowerCase().includes(q) ||
      sku.uom?.toLowerCase().includes(q)
    );
  }, [inventory, invSearch]);

  const totalPages = Math.ceil(filteredInventory.length / PAGE_SIZE);
  const paginatedInventory = filteredInventory.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // When deep-linked via ?sku=<id>, jump to the page containing that SKU
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const target = params.get('sku');
    if (!target || filteredInventory.length === 0) return;
    const idx = filteredInventory.findIndex(s => s.id === target);
    if (idx === -1) return;
    const page = Math.floor(idx / PAGE_SIZE) + 1;
    if (page !== currentPage) setCurrentPage(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredInventory]);

  const filteredIssues = useMemo(() => {
    if (!issuesSearch.trim()) return issues;
    const q = issuesSearch.toLowerCase();
    return issues.filter(issue =>
      issue.sku_particulars?.toLowerCase().includes(q) ||
      issue.issued_to_door_no?.toLowerCase().includes(q) ||
      issue.created_by?.toLowerCase().includes(q) ||
      issue.notes?.toLowerCase().includes(q)
    );
  }, [issues, issuesSearch]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Inventory Management</h1>
          <p className="text-slate-600 mt-1">Track SKUs, quantities, and purchase history</p>
        </div>
        {canEdit && activeTab === 'stock' && (
          <div className="flex gap-2">
            <Button
              onClick={() => setIsImportOpen(true)}
              variant="outline"
              data-testid="import-inventory-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              Import Excel
            </Button>
            <Button
              onClick={() => { resetSKUForm(); setIsAddSKUOpen(true); }}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="add-sku-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add SKU
            </Button>
          </div>
        )}
      </div>

      <PendingDeletionsPanel module="inventory" requests={pendingDelRequests} onApprove={handleApproveDelete} onReject={handleRejectDelete} />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="stock" data-testid="tab-stock">
            <Package className="h-4 w-4 mr-2" />
            Stock
          </TabsTrigger>
          <TabsTrigger value="issues" data-testid="tab-issues">
            <History className="h-4 w-4 mr-2" />
            Issues History
          </TabsTrigger>
        </TabsList>

        {/* ── STOCK TAB ── */}
        <TabsContent value="stock">
      {inventory.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-lg border">
          <Package className="h-12 w-12 text-slate-400 mx-auto mb-4" />
          <p className="text-slate-500">No inventory items yet. Add your first SKU to get started!</p>
        </div>
      ) : (
        <>
          <FilterBar
            onReset={() => setInvSearch('')}
            activeFiltersCount={invSearch ? 1 : 0}
          >
            <SearchFilter
              label="Search SKUs"
              value={invSearch}
              onChange={setInvSearch}
              placeholder="Particulars, part number, machinery group, make..."
            />
          </FilterBar>
          {invSearch && (
            <p className="text-sm text-slate-500 -mt-3 mb-1">
              Showing {filteredInventory.length} of {inventory.length} items
            </p>
          )}
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-500">{filteredInventory.length} SKUs</span>
            <ExportButton
              rows={filteredInventory}
              columns={[
                { header: 'S.No', key: (r, i) => i + 1 },
                { header: 'Machinery Group', key: 'machinery_group' },
                { header: 'Part Name', key: 'particulars' },
                { header: 'Part Number', key: 'part_number' },
                { header: 'Make', key: 'make' },
                { header: 'UOM', key: 'uom' },
                { header: 'Current Qty', key: 'current_quantity' },
                { header: 'Min Qty', key: 'min_quantity' },
                { header: 'Unit Price (₹)', key: r => (r.unit_price || r.average_price || 0).toFixed(2) },
                { header: 'Total Value (₹)', key: r => ((r.current_quantity || 0) * (r.unit_price || r.average_price || 0)).toFixed(2) },
                { header: 'Category', key: 'category' },
              ]}
              filename={`inventory_${new Date().toISOString().slice(0,10)}`}
              title="Store Opening Stock"
              subtitle={`Exported on ${new Date().toLocaleDateString('en-IN')}`}
            />
          </div>
        <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12"></TableHead>
                <TableHead className="w-12">S.No</TableHead>
                <TableHead>Machinery Group</TableHead>
                <TableHead>Part Name</TableHead>
                <TableHead>Part Number</TableHead>
                <TableHead>Make</TableHead>
                <TableHead>UOM</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Unit Price</TableHead>
                <TableHead className="text-right">Total Value</TableHead>
                {canEdit && <TableHead className="w-28">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedInventory.map((sku, idx) => (
                <React.Fragment key={sku.id}>
                  <TableRow 
                    className={`hover:bg-slate-50 cursor-pointer ${isLowStock(sku) ? 'bg-red-50' : ''}`}
                    onClick={() => toggleExpand(sku.id)}
                    data-testid={`sku-row-${sku.id}`}
                  >
                    <TableCell>
                      {expandedSKU === sku.id ? (
                        <ChevronDown className="h-4 w-4 text-slate-500" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-slate-500" />
                      )}
                    </TableCell>
                    <TableCell>{(currentPage - 1) * PAGE_SIZE + idx + 1}</TableCell>
                    <TableCell>
                      <span className="text-xs font-medium bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
                        {sku.machinery_group || '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{sku.particulars}</span>
                        {isLowStock(sku) && (
                          <AlertTriangle className="h-4 w-4 text-red-500" title="Low Stock" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">{sku.part_number || '-'}</TableCell>
                    <TableCell>{sku.make || '-'}</TableCell>
                    <TableCell>{sku.uom || '-'}</TableCell>
                    <TableCell className="text-right">
                      <span className={isLowStock(sku) ? 'text-red-600 font-semibold' : ''}>
                        {sku.current_quantity}
                      </span>
                      {sku.min_quantity > 0 && (
                        <span className="text-xs text-slate-500 ml-1">(min: {sku.min_quantity})</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">₹{(sku.unit_price || sku.average_price || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right font-medium">
                      ₹{((sku.current_quantity || 0) * (sku.unit_price || sku.average_price || 0)).toLocaleString('en-IN', {minimumFractionDigits: 0, maximumFractionDigits: 0})}
                    </TableCell>
                    {canEdit && (
                      <TableCell>
                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openPurchaseModal(sku)}
                            title="Add Purchase"
                          >
                            <ShoppingCart className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditModal(sku)}
                            title="Edit"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700"
                          onClick={() => handleDeleteSKU(sku)}
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                  
                  {/* Expanded Purchase History */}
                  {expandedSKU === sku.id && (
                    <TableRow>
                      <TableCell colSpan={canEdit ? 11 : 10} className="bg-slate-50 p-0">
                        <div className="p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-semibold text-slate-900">Purchase History</h4>
                            <div className="text-sm text-slate-600">
                              Price Range: ₹{sku.lowest_price?.toFixed(2) || '0.00'} - ₹{sku.highest_price?.toFixed(2) || '0.00'}
                            </div>
                          </div>
                          {sku.description && (
                            <p className="text-sm text-slate-600 mb-3">{sku.description}</p>
                          )}
                          {loadingPurchases ? (
                            <div className="flex justify-center py-4">
                              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                            </div>
                          ) : purchases.length === 0 ? (
                            <p className="text-sm text-slate-500 text-center py-4">No purchase history</p>
                          ) : (
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Date</TableHead>
                                  <TableHead>Invoice #</TableHead>
                                  <TableHead>Vendor</TableHead>
                                  <TableHead className="text-right">Qty</TableHead>
                                  <TableHead className="text-right">Unit Price</TableHead>
                                  <TableHead className="text-right">Total</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {purchases.map((p) => (
                                  <TableRow key={p.id}>
                                    <TableCell>{format(new Date(p.purchase_date), 'MMM dd, yyyy')}</TableCell>
                                    <TableCell>{p.invoice_number || '-'}</TableCell>
                                    <TableCell>{p.vendor_name || '-'}</TableCell>
                                    <TableCell className="text-right">{p.quantity}</TableCell>
                                    <TableCell className="text-right">₹{p.unit_price?.toFixed(2)}</TableCell>
                                    <TableCell className="text-right">₹{(p.quantity * p.unit_price).toFixed(2)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-2 py-3 border-t mt-2">
            <p className="text-sm text-slate-500">
              Showing {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, filteredInventory.length)} of <span className="font-medium">{filteredInventory.length}</span> items
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-40 hover:bg-slate-50 transition-colors"
              >
                Previous
              </button>
              <span className="text-sm text-slate-600">Page {currentPage} of {totalPages}</span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-40 hover:bg-slate-50 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
        </>
      )}
        </TabsContent>

        {/* ── ISSUES HISTORY TAB ── */}
        <TabsContent value="issues">
          <div className="space-y-4">
            <FilterBar
              onReset={() => {
                setIssuesDateRange({ from: null, to: null });
                setIssuesSearch('');
              }}
              activeFiltersCount={(issuesSearch ? 1 : 0) + (issuesDateRange.from || issuesDateRange.to ? 1 : 0)}
            >
              <DateRangeFilter
                label="Date Range"
                value={issuesDateRange}
                onChange={setIssuesDateRange}
              />
              <SearchFilter
                label="Search"
                value={issuesSearch}
                onChange={setIssuesSearch}
                placeholder="SKU, door no, issued by..."
              />
            </FilterBar>

            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500" data-testid="issues-count">
                {filteredIssues.length} issue{filteredIssues.length !== 1 ? 's' : ''}
              </span>
              <ExportButton
                rows={filteredIssues}
                columns={[
                  { header: 'Date', key: r => r.created_at ? format(new Date(r.created_at), 'dd MMM yyyy') : '' },
                  { header: 'SKU / Part', key: 'sku_particulars' },
                  { header: 'Qty Issued', key: 'quantity' },
                  { header: 'Issued To (Door No)', key: 'issued_to_door_no' },
                  { header: 'Received By', key: 'issued_to_name' },
                  { header: 'Maintenance Log ID', key: 'maintenance_log_id' },
                  { header: 'Issued By', key: 'created_by' },
                  { header: 'Notes', key: 'notes' },
                ]}
                filename={`inventory_issues_${new Date().toISOString().slice(0,10)}`}
                title="Inventory Issues Report"
                subtitle={`Exported on ${new Date().toLocaleDateString('en-IN')}`}
              />
            </div>

            {loadingIssues ? (
              <div className="flex justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : filteredIssues.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-lg border">
                <History className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-500">No inventory issues found for this period.</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg border shadow-sm overflow-hidden" data-testid="issues-table">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>SKU / Part</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead>Issued To (Asset)</TableHead>
                      <TableHead>Received By</TableHead>
                      <TableHead>Issued By</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredIssues.map((issue) => (
                      <TableRow key={issue.id} data-testid={`issue-row-${issue.id}`}>
                        <TableCell className="text-sm">
                          {issue.created_at ? format(new Date(issue.created_at), 'dd MMM yyyy') : '-'}
                        </TableCell>
                        <TableCell className="font-medium">{issue.sku_particulars || '-'}</TableCell>
                        <TableCell className="text-right font-semibold">{issue.quantity}</TableCell>
                        <TableCell>{issue.issued_to_door_no || '-'}</TableCell>
                        <TableCell className="font-medium text-emerald-700">{issue.issued_to_name || '-'}</TableCell>
                        <TableCell className="text-slate-600 text-sm">{issue.created_by || '-'}</TableCell>
                        <TableCell className="text-slate-500 text-sm max-w-xs truncate">{issue.notes || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Add SKU Modal */}
      <Dialog open={isAddSKUOpen} onOpenChange={setIsAddSKUOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add New SKU</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddSKU} className="space-y-4">
            <div>
              <Label>Machinery Group *</Label>
              <Input
                value={skuForm.machinery_group}
                onChange={(e) => setSkuForm({ ...skuForm, machinery_group: e.target.value })}
                required
                placeholder="e.g. Volvo Truck 440, AC Electrical Section"
                className="mt-1.5"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Part Name *</Label>
                <Input
                  value={skuForm.particulars}
                  onChange={(e) => setSkuForm({ ...skuForm, particulars: e.target.value })}
                  required
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Part Number</Label>
                <Input
                  value={skuForm.part_number}
                  onChange={(e) => setSkuForm({ ...skuForm, part_number: e.target.value })}
                  className="mt-1.5"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Make</Label>
                <Input
                  value={skuForm.make}
                  onChange={(e) => setSkuForm({ ...skuForm, make: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>UOM</Label>
                <Input
                  value={skuForm.uom}
                  onChange={(e) => setSkuForm({ ...skuForm, uom: e.target.value })}
                  placeholder="No's, Ltrs, Kgs..."
                  className="mt-1.5"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Unit Price (₹)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={skuForm.unit_price}
                  onChange={(e) => setSkuForm({ ...skuForm, unit_price: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={skuForm.category || 'Spares'} onValueChange={v => setSkuForm({ ...skuForm, category: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['Spares', 'Tyres', 'Diesel', 'Scrap', 'General'].map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Opening Qty</Label>
                <Input
                  type="number"
                  min="0"
                  value={skuForm.current_quantity}
                  onChange={(e) => setSkuForm({ ...skuForm, current_quantity: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Min Qty (for alerts)</Label>
                <Input
                  type="number"
                  min="0"
                  value={skuForm.min_quantity}
                  onChange={(e) => setSkuForm({ ...skuForm, min_quantity: e.target.value })}
                  className="mt-1.5"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsAddSKUOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800">
                Add SKU
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit SKU Modal */}
      <Dialog open={isEditSKUOpen} onOpenChange={setIsEditSKUOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit SKU — {selectedSKU?.particulars}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSKU} className="space-y-4">
            {/* Identification */}
            <div>
              <Label>Machinery Group</Label>
              <Input
                value={skuForm.machinery_group}
                onChange={(e) => setSkuForm({ ...skuForm, machinery_group: e.target.value })}
                className="mt-1.5"
                placeholder="e.g. Volvo Truck 440"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Part Name *</Label>
                <Input
                  value={skuForm.particulars}
                  onChange={(e) => setSkuForm({ ...skuForm, particulars: e.target.value })}
                  required
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Part Number</Label>
                <Input
                  value={skuForm.part_number}
                  onChange={(e) => setSkuForm({ ...skuForm, part_number: e.target.value })}
                  className="mt-1.5"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Make</Label>
                <Input
                  value={skuForm.make}
                  onChange={(e) => setSkuForm({ ...skuForm, make: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Model</Label>
                <Input
                  value={skuForm.model}
                  onChange={(e) => setSkuForm({ ...skuForm, model: e.target.value })}
                  className="mt-1.5"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>UOM</Label>
                <Input
                  value={skuForm.uom}
                  onChange={(e) => setSkuForm({ ...skuForm, uom: e.target.value })}
                  placeholder="No's, Ltrs, Kgs..."
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={skuForm.category || 'Spares'} onValueChange={v => setSkuForm({ ...skuForm, category: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['Spares', 'Tyres', 'Diesel', 'Scrap', 'General'].map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Pricing & Stock */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Unit Price (₹)</Label>
                <Input
                  type="number" min="0" step="0.01"
                  value={skuForm.unit_price}
                  onChange={(e) => setSkuForm({ ...skuForm, unit_price: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Current Qty</Label>
                <Input
                  type="number" min="0" step="0.01"
                  value={skuForm.current_quantity}
                  onChange={(e) => setSkuForm({ ...skuForm, current_quantity: e.target.value })}
                  className="mt-1.5"
                />
              </div>
            </div>
            <div>
              <Label>Min Qty (for low-stock alert)</Label>
              <Input
                type="number" min="0" step="0.01"
                value={skuForm.min_quantity}
                onChange={(e) => setSkuForm({ ...skuForm, min_quantity: e.target.value })}
                className="mt-1.5"
              />
            </div>
            {/* Optional details */}
            <div>
              <Label>Vendor Name</Label>
              <Input
                value={skuForm.vendor_name}
                onChange={(e) => setSkuForm({ ...skuForm, vendor_name: e.target.value })}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Description / Notes</Label>
              <Textarea
                value={skuForm.description}
                onChange={(e) => setSkuForm({ ...skuForm, description: e.target.value })}
                className="mt-1.5"
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setIsEditSKUOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800">
                Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Purchase Modal */}
      <Dialog open={isAddPurchaseOpen} onOpenChange={setIsAddPurchaseOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Purchase - {selectedSKU?.particulars}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddPurchase} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantity *</Label>
                <Input
                  type="number"
                  min="1"
                  value={purchaseForm.quantity}
                  onChange={(e) => setPurchaseForm({ ...purchaseForm, quantity: e.target.value })}
                  required
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Unit Price (₹) *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={purchaseForm.unit_price}
                  onChange={(e) => setPurchaseForm({ ...purchaseForm, unit_price: e.target.value })}
                  required
                  className="mt-1.5"
                />
              </div>
            </div>
            <div>
              <Label>Purchase Date *</Label>
              <Input
                type="date"
                value={purchaseForm.purchase_date}
                onChange={(e) => setPurchaseForm({ ...purchaseForm, purchase_date: e.target.value })}
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Invoice Number</Label>
              <Input
                value={purchaseForm.invoice_number}
                onChange={(e) => setPurchaseForm({ ...purchaseForm, invoice_number: e.target.value })}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Vendor Name</Label>
              <Input
                value={purchaseForm.vendor_name}
                onChange={(e) => setPurchaseForm({ ...purchaseForm, vendor_name: e.target.value })}
                className="mt-1.5"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsAddPurchaseOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800">
                Record Purchase
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={isImportOpen} onOpenChange={setIsImportOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Inventory from Excel</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Upload an Excel file (.xlsx, .xls) or CSV with columns:
            </p>
            <ul className="text-sm text-slate-500 list-disc list-inside">
              <li>particulars</li>
              <li>part_number</li>
              <li>make</li>
              <li>model</li>
              <li>description (optional)</li>
              <li>vendor_name</li>
              <li>min_quantity (optional)</li>
              <li>current_quantity (optional)</li>
            </ul>
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileUpload}
                disabled={uploading}
                className="hidden"
                id="inventory-file-upload"
              />
              <label htmlFor="inventory-file-upload" className="cursor-pointer">
                {uploading ? (
                  <Loader2 className="h-8 w-8 mx-auto text-slate-400 animate-spin" />
                ) : (
                  <Upload className="h-8 w-8 mx-auto text-slate-400" />
                )}
                <p className="mt-2 text-sm text-slate-600">
                  {uploading ? 'Uploading...' : 'Click to upload or drag and drop'}
                </p>
              </label>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteRequestDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        module="inventory"
        entryId={deleteTarget?.id}
        entryDisplay={deleteTarget?.display}
        onSuccess={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default InventoryManagement;
